import React, { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FlashHotelRoomDetailsModal from '../FlashHotel/FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import ModalContext from 'contexts/ModalContext'

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  isLoggedIn: boolean;
  checkIn?: string;
  currency: string;
  buyNowBookLater: boolean;
  room: App.RoomOccupants;
  isFlightsEnabled?: boolean;
  packageOption: OfferPackageOptionView;
  isFetching?: boolean;
  isFlightsSelected?: boolean;
  isUpgradable?: boolean;
  availablePackagesQuantity?: number;
  openRoomsModal: (room: App.RoomOccupants) => void;
  defaultDepositAmountPercentage: number;
}

function MultipleRoomsBookingPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    checkIn,
    currency,
    buyNowBookLater,
    room,
    isFlightsEnabled,
    packageOption,
    isFetching,
    isFlightsSelected,
    isUpgradable,
    availablePackagesQuantity = 0,
    openRoomsModal,
    defaultDepositAmountPercentage,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageOption

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330
  }, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue
  const shouldShowBNBL = packageOption.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && buyNowBookLater
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, (showMemberPrice ? hotelMemberPrice : hotelPrice)) && !buyNowBookLater

  const handleChangePackage = useCallback(() => {
    openRoomsModal(room)
  }, [openRoomsModal, room])

  const showFlightsPricing = hasFlights || (!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView: packageOption }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView: packageOption, skip: !isFlightsSelected }),
  ], [offer, packageOption, isFlightsSelected])

  const onShowRoomDetails = useCallback(() => {
    if (pkg.roomType) {
      showModal(<FlashHotelRoomDetailsModal room={pkg.roomType} />)
    }
  }, [pkg.roomType, showModal])

  return (
    <Group direction="vertical" gap={12}>
      {shouldShowBNBL && (
        <StructureLabel startIcon={<LineCheckIcon size="XS" />} variant="light">Buy now, choose dates later</StructureLabel>
      )}
      {isDepositEnabled && <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>}
      <Group direction="vertical" gap={12}>
        {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="pricing">
              <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} {!hasFlights && 'from'}</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice}
                memberPrice={totalMemberPrice}
                saleUnit="total"
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </Group>
            <LoyaltyPoints
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
            />
          </Group>
        )}

        {isFlightsEnabled && isFlightsSelected && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="flights-pricing">
              {!hasFlights && flightOrigin?.code === flightDestination?.code && (
                <Group direction="vertical" gap={8}>
                  <PriceRowCaption>
                    No flights available from {flightOrigin?.name}. Please select another departing airport.
                  </PriceRowCaption>
                  <TextButton
                    kind="tertiary"
                    onClick={onToggleFilter}
                    data-filter="airport"
                  >
                    Change airport
                  </TextButton>
                </Group>
              )}
              {!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture && (
                <Group direction="vertical" gap={4}>
                  <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                    {pluralizeToString('night', pkg.duration)} + flights from
                  </PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalPrice}
                    saleUnit="total*"
                    memberPrice={totalMemberPrice}
                    offerConfig={offer.luxPlus}
                  />
                  <PriceRowTaxesAndFees />
                </Group>
              )}
              {!hasFlights && departureTooFarInFuture && (
                <Group direction="vertical" gap={8}>
                  <PriceRowCaption>
                    Flights can only be booked up to 11 months in advance.
                  </PriceRowCaption>
                </Group>
              )}
              {hasFlights && (
                <Group direction="vertical" gap={4}>
                  <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                    {pluralizeToString('night', pkg.duration)} + flights from
                  </PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalPrice + flightPrice}
                    memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                    saleUnit="total*"
                    offerConfig={offer.luxPlus}
                  />
                  <PriceRowTaxesAndFees />
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="M"
                    originalValue={hotelValue + flightPrice}
                  />}
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                </Group>
              )}
              <AtolProtectedTooltip />
            </Group>
            {showFlightsPricing && (
              <LoyaltyPoints
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
              />
            )}
          </Group>
        )}
        <LoadingIndicator visible={isFetching} floating opaque />
      </Group>
      {isUpgradable && (
        <Group direction="vertical" gap={12}>
          <CSSBreakpoint max="mobile">
            <Group direction="horizontal" horizontalAlign="stretch" gap={8}>
              {pkg.roomType && <TextButton kind="tertiary" size="large" onClick={onShowRoomDetails}>View details</TextButton>}
              <TextButton kind="secondary" size="large" onClick={handleChangePackage}>
                Change room
              </TextButton>
            </Group>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <TextButton kind="secondary" fit="flex" size="large" onClick={handleChangePackage}>
              Change room
            </TextButton>
          </CSSBreakpoint>
          {availablePackagesQuantity > 0 &&
            <Group direction="horizontal" horizontalAlign="center">
              <PriceRowCaption>
                {pluralizeToString('option', availablePackagesQuantity)} available
              </PriceRowCaption>
            </Group>
          }
        </Group>
      )}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(MultipleRoomsBookingPriceDetails)
